import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'

import { FamilyPage } from '@/components/family/FamilyPage'
import { useAuth, useClientProject } from '@/contexts'
import { goToContract } from '@/lib/clauseNav'
import { isUuid, normalizeEntityId, toShortEntityId } from '@/lib/entityUrl'
import { getProjectAccessLevel } from '@/services/userManagementService'
import { isGlobalAdmin } from '@/types'
import { useVaultContext } from './-vault-context'

/**
 * A Contract Family's page (ADR 0051), beside the document page. The URL carries the family's uuid
 * in its short form; the project comes from the vault's `?projectId=`, which every link to this page
 * sets, and the vault layout loads that project's documents behind the page.
 */
export const Route = createFileRoute('/_app/law/vault/family/$familyId')({
  head: () => ({ meta: [{ title: 'Contract Family · AERRIS' }] }),
  component: FamilyRoute,
  params: {
    parse: (params) => ({ familyId: normalizeEntityId(params.familyId) ?? params.familyId }),
    stringify: (params) => ({ familyId: toShortEntityId(params.familyId) }),
  },
  // A dashed-UUID link is put into the short form; replace, so it doesn't linger in history.
  beforeLoad: ({ params, location }) => {
    if (isUuid(location.pathname.split('/').pop() ?? '')) {
      throw redirect({
        to: '/law/vault/family/$familyId',
        params: { familyId: params.familyId },
        search: true,
        replace: true,
      })
    }
  },
})

function FamilyRoute() {
  const { familyId } = Route.useParams()
  const navigate = useNavigate()
  const { projectId } = useClientProject()
  const projectUuid = projectId ? normalizeEntityId(projectId) ?? projectId : undefined
  const { contracts } = useVaultContext()
  const { currentUser } = useAuth()
  const canEdit = useCanEditProject(projectUuid, isGlobalAdmin(currentUser?.role))

  if (!projectUuid) {
    return (
      <div className="family-page__state">
        Open this family from its project's contracts list.
      </div>
    )
  }

  return (
    <FamilyPage
      key={familyId}
      projectUuid={projectUuid}
      familyUuid={familyId}
      canEdit={canEdit}
      contracts={contracts}
      onOpenDocument={(contractUuid, clauseUuid) => goToContract(navigate, contractUuid, clauseUuid)}
      onBack={() => navigate({ to: '/law/vault', search: { projectId: toShortEntityId(projectUuid) } })}
    />
  )
}

/** EDITOR or better on the project (a global admin always): the same gate as the vault's actions. */
function useCanEditProject(projectUuid: string | undefined, globalAdmin: boolean): boolean {
  const [state, setState] = useState<{ projectUuid: string; canEdit: boolean } | null>(null)
  useEffect(() => {
    if (globalAdmin || !projectUuid) return
    let cancelled = false
    getProjectAccessLevel(projectUuid)
      .then((level) => { if (!cancelled) setState({ projectUuid, canEdit: level === 'ADMIN' || level === 'EDITOR' }) })
      .catch(() => { if (!cancelled) setState({ projectUuid, canEdit: false }) })
    return () => { cancelled = true }
  }, [projectUuid, globalAdmin])
  if (globalAdmin) return true
  return state !== null && state.projectUuid === projectUuid ? state.canEdit : false
}
